import {
  ActivityFilterKey,
  ActivityFilterModel,
  ActivityOptions,
  useActivityListFilter,
  useCurrentWorkspace,
  useSetActivityListFilter,
  useWorkspaceLabels,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';
import { array, string, object as yupObject } from 'yup';
import { FormContext } from '../forms';
import { DropdownFooter } from '../modals/dropdownFooter';
import { ApplyButton } from './applyButton';
import { EditableItemFilter } from './editableItemFilter';
import { FilterType } from './filterButton';
import { FilterHeader } from './filterHeader';
import { filterStyles as styles } from './filterStyle';
import { LabelSearchInput } from './labelSearchInput';
import { useEditableItemFilter } from './useEditableItemFilter';

const UPDATE_LABEL_MUTATION = `
  mutation UpdateLabel($input: MutateLabelInput!) {
    updateLabel(input: $input) {
      id
      name
    }
  }
`;

const DELETE_LABEL_MUTATION = `
  mutation DeleteLabel($input: MutateLabelInput!) {
    deleteLabel(input: $input) {
      id
    }
  }
`;

const ISSUES_BY_LABEL_QUERY = `
  query IssuesByLabel($filter: FindIssuesFilter) {
    issue {
      findIssues(filter: $filter) {
        count
      }
    }
  }
`;

interface ActivityLabelsFilterScreenProps {
  filterKey: ActivityFilterKey;
  goBack: () => void;
  type: FilterType;
}

export const ActivityLabelsFilter = ({
  filterKey,
  goBack,
  type,
}: ActivityLabelsFilterScreenProps) => {
  const { t } = useTranslation();
  const workspace = useCurrentWorkspace(true);
  const { data: filter } = useActivityListFilter(filterKey);
  const { mutateAsync: setActivityListFilter } =
    useSetActivityListFilter(filterKey);

  const isWeb = Platform.OS === 'web';
  const workspaceId = typeof workspace === 'string' ? workspace : workspace?.id;

  const formSchema = yupObject({
    orderBy: string().optional().nullable(),
    labelIds: array().of(string()).optional().nullable(),
    activityOption: string().optional().nullable(),
  });

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      orderBy: filter?.orderBy,
      labelIds: filter?.labelIds,
      activityOption: filter?.activityOption || ActivityOptions.EVERYTHING,
    },
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
    watch,
  } = formMethods;

  const formData = watch();

  const isLabelsFilter = type === FilterType.ACTIVITYLABELS;

  const resetForm = useCallback(() => {
    reset({
      ...formData,
      ...(isLabelsFilter && { labelIds: [] }),
      activityOption: ActivityOptions.EVERYTHING,
    });
  }, [reset, formData, isLabelsFilter]);

  const onReset = async () => {
    const updatedFields = {
      ...(isLabelsFilter && { labelIds: [] }),
      activityOption: ActivityOptions.EVERYTHING,
    };

    resetForm();

    const mergedFilter = { ...filter, ...updatedFields };
    await setActivityListFilter(mergedFilter);
    goBack();
  };

  const onSubmit = async (data: any) => {
    await setActivityListFilter(
      new ActivityFilterModel({ ...filter, ...data })
    );
    goBack();
  };

  const onPressLabelFilter = (
    labelId: string,
    value: (string | undefined)[],
    onChange: (...event: any[]) => void
  ) => {
    let newValue = [];
    if (!value) {
      newValue = [labelId];
    } else if (!value.includes(labelId)) {
      newValue = value.concat(labelId);
    } else {
      newValue = value.filter((id: string | undefined) => id !== labelId);
    }
    onChange(newValue);
  };

  const canReset = useCallback(() => {
    const orderBy = formData.orderBy;
    const labelIds = formData.labelIds;
    const activityOption = formData.activityOption;
    const defaultFilter = new ActivityFilterModel();

    const undefinedIfEmpty = (value?: (string | undefined)[] | null) => {
      return value?.length === 0 ? undefined : value;
    };

    return (
      orderBy !== defaultFilter.orderBy ||
      undefinedIfEmpty(labelIds) !== defaultFilter.labelIds ||
      activityOption !== defaultFilter.activityOption
    );
  }, [formData]);

  useEffect(() => {
    if (!filter) {
      resetForm();
    }
  }, [filter, resetForm]);

  // Initialize hook with empty editingLabelId first
  // Search state
  const [search, setSearch] = useState('');
  const [isConfirmDeleteVisible, setIsConfirmDeleteVisible] = useState(false);
  const [filteredLabels, setFilteredLabels] = useState<
    Array<{ id: string; name: string }>
  >([]);

  const {
    items: allLabels,
    editingItemId: editingLabelId,
    editingItemName: editingLabelName,
    openEditMenu,
    setEditingItemName: setEditingLabelName,
    handleDelete,
    onConfirmDelete,
    confirmDeleteVisible,
    onConfirmModalClose,
    closeEditMenu,
    anchorPos,
    itemRefs,
    itemCount,
  } = useEditableItemFilter({
    itemType: 'label',
    workspaceId,
    useWorkspaceItemsHook: useWorkspaceLabels,
    updateMutation: { modelName: 'Label', query: UPDATE_LABEL_MUTATION },
    deleteMutation: { query: DELETE_LABEL_MUTATION },
    countQuery: {
      cacheKey: ['activitiesByLabel'],
      query: ISSUES_BY_LABEL_QUERY,
      variables: {
        filter: {
          ...(filter?.labelIds && { labelIds: filter.labelIds }),
          workspace: {
            eq: workspaceId,
          },
        },
      },
      options: {
        enabled: !!workspaceId && isConfirmDeleteVisible && !!filter?.labelIds,
      },
    },
    refetchItems: () => {}, // Placeholder, actual refetch is handled by useWorkspaceLabels
    setFilter: async (newFilter: any) => {
      await setActivityListFilter(
        new ActivityFilterModel({ ...filter, ...newFilter })
      );
    },
    currentFilter: filter,
  });

  useEffect(() => {
    setIsConfirmDeleteVisible(confirmDeleteVisible);
  }, [confirmDeleteVisible]);

  // Filter labels based on search
  useEffect(() => {
    const filtered = allLabels.filter(
      (label: any) =>
        label &&
        label.name &&
        label.name.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredLabels(filtered);
  }, [allLabels, search]);

  return (
    <FormContext.Provider value={formMethods}>
      {!isWeb && (
        <FilterHeader canReset={canReset} onReset={onReset} goBack={goBack} />
      )}
      <View style={styles.container}>
        <View
          style={[
            styles.scrollViewContainer,
            isWeb ? { height: 416 } : { marginBottom: 30 },
          ]}
        >
          <Controller
            control={control}
            name={'labelIds'}
            render={({ field: { onChange, value } }) => (
              <EditableItemFilter
                items={filteredLabels}
                selectedIds={value || []}
                onItemToggle={onPressLabelFilter}
                onChange={onChange}
                editingItemId={editingLabelId}
                editingItemName={editingLabelName}
                onEditItem={openEditMenu}
                onEditNameChange={setEditingLabelName}
                onDeleteItem={handleDelete}
                onConfirmDelete={onConfirmDelete}
                confirmDeleteVisible={confirmDeleteVisible}
                onConfirmModalClose={onConfirmModalClose}
                onCloseEditMenu={closeEditMenu}
                anchorPos={anchorPos}
                itemRefs={itemRefs}
                searchComponent={
                  <LabelSearchInput value={search} onChangeText={setSearch} />
                }
                editTitle={t('labels.editLabels')}
                deleteButtonText={t('labels.delete')}
                confirmTextLine1={t('labels.confirmTextLine1', {
                  labelName: editingLabelName || '',
                })}
                confirmTextLine2={t('labels.confirmTextLine2', {
                  activityCount: itemCount || 0,
                })}
                yesText={t('labels.yes')}
                cancelText={t('labels.cancel')}
                itemCount={itemCount}
              />
            )}
          />
        </View>

        {isWeb ? (
          <DropdownFooter
            isDirty={isDirty || canReset()}
            onSubmit={handleSubmit(onSubmit)}
            canReset={canReset}
            onReset={onReset}
          />
        ) : (
          <ApplyButton
            isDirty={isDirty || canReset()}
            onSubmit={handleSubmit(onSubmit)}
          />
        )}
      </View>
    </FormContext.Provider>
  );
};
